import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Award, Briefcase, Building2, FileText, Globe, Layers, LayoutDashboard, LogOut,
  Newspaper, Users, UsersRound, Workflow,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import CrudTab from "./CrudTab";
import LeadsTab from "./LeadsTab";
import ApplicationsTab from "./ApplicationsTab";
import SettingsTab from "./SettingsTab";

const JOB_FIELDS = [
  { name: "title", label: "Job Title" },
  { name: "department", label: "Department" },
  { name: "experience", label: "Experience (e.g. 3-6 years)" },
  { name: "employment_type", label: "Employment Type", type: "select", options: ["Full-time", "Part-time", "Contract", "Internship"], default: "Full-time" },
  { name: "work_mode", label: "Work Mode", type: "select", options: ["Remote", "Hybrid", "On-site"], default: "Hybrid" },
  { name: "location", label: "Location" },
  { name: "application_deadline", label: "Application Deadline (e.g. 2026-08-31)" },
  { name: "status", label: "Status", type: "select", options: ["open", "paused", "closed"], default: "open" },
  { name: "description", label: "Job Description", type: "textarea" },
  { name: "required_skills", label: "Required Skills (one per line)", type: "list" },
  { name: "responsibilities", label: "Responsibilities (one per line)", type: "list" },
  { name: "qualifications", label: "Qualifications (one per line)", type: "list" },
  { name: "preferred_skills", label: "Preferred Skills (one per line)", type: "list" },
];

const SERVICE_FIELDS = [
  { name: "name", label: "Service Name" },
  { name: "slug", label: "Slug (url)" },
  { name: "menu_category", label: "Mega Menu Category" },
  { name: "order", label: "Order", type: "number" },
  { name: "tagline", label: "Hero Headline" },
  { name: "summary", label: "Short Summary", type: "textarea", rows: 2 },
  { name: "what", label: "What TARK Provides", type: "textarea", rows: 5 },
  { name: "capabilities", label: "Capabilities (one per line)", type: "list" },
  { name: "technologies", label: "Technologies (one per line)", type: "list", rows: 3 },
  { name: "problems", label: "Problems Solved (one per line)", type: "list" },
  { name: "solutions", label: "Typical Solutions Delivered (one per line)", type: "list" },
  { name: "audience", label: "Who This Is For (one per line)", type: "list" },
  { name: "published", label: "Visibility", type: "checkbox" },
];

const INDUSTRY_FIELDS = [
  { name: "name", label: "Industry Name" },
  { name: "slug", label: "Slug (url)" },
  { name: "order", label: "Order", type: "number" },
  { name: "published", label: "Visibility", type: "checkbox" },
  { name: "headline", label: "Headline", type: "textarea", rows: 2 },
  { name: "description", label: "Description", type: "textarea", rows: 4 },
  { name: "challenges", label: "Challenges (one per line)", type: "list" },
  { name: "offerings", label: "What TARK Delivers (one per line)", type: "list" },
];

const INSIGHT_FIELDS = [
  { name: "title", label: "Title" },
  { name: "slug", label: "Slug (url)" },
  { name: "tag", label: "Tag", default: "Engineering" },
  { name: "published", label: "Visibility", type: "checkbox" },
  { name: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
  { name: "content", label: "Content (blank line between paragraphs)", type: "textarea", rows: 10 },
];

const STAGE_FIELDS = [
  { name: "title", label: "Stage Title" },
  { name: "order", label: "Order", type: "number" },
  { name: "description", label: "Description", type: "textarea", rows: 3 },
];

const DEPARTMENT_FIELDS = [{ name: "name", label: "Department / Location Name" }];

const LEADERSHIP_FIELDS = [
  { name: "name", label: "Name" },
  { name: "role", label: "Role" },
  { name: "order", label: "Order", type: "number" },
  { name: "photo_url", label: "Photo URL" },
  { name: "bio", label: "Bio", type: "textarea", rows: 4 },
];

const CASE_STUDY_FIELDS = [
  { name: "title", label: "Title" },
  { name: "industry", label: "Industry (e.g. Healthcare)" },
  { name: "published", label: "Visibility", type: "checkbox" },
  { name: "summary", label: "Summary", type: "textarea", rows: 3 },
  { name: "outcomes", label: "Outcomes (one per line)", type: "list" },
  { name: "tags", label: "Tags (one per line)", type: "list", rows: 2 },
];

const SECTIONS = [
  {
    group: "Client Side",
    items: [
      { id: "leads", label: "Inquiries", icon: FileText, el: <LeadsTab /> },
      { id: "case-studies", label: "Case Studies", icon: Award, el: <CrudTab endpoint="case-studies" title="Case Studies" fields={CASE_STUDY_FIELDS} testId="case-studies" columns={[{ key: "title", label: "Title" }, { key: "industry", label: "Industry" }]} /> },
      { id: "services", label: "Services", icon: Layers, el: <CrudTab endpoint="services" title="Services" fields={SERVICE_FIELDS} testId="services" columns={[{ key: "name", label: "Name" }, { key: "slug", label: "Slug" }, { key: "order", label: "Order" }]} /> },
      { id: "industries", label: "Industries", icon: Building2, el: <CrudTab endpoint="industries" title="Industries" fields={INDUSTRY_FIELDS} testId="industries" columns={[{ key: "name", label: "Name" }, { key: "slug", label: "Slug" }, { key: "order", label: "Order" }]} /> },
      { id: "insights", label: "Insights", icon: Newspaper, el: <CrudTab endpoint="insights" title="Insights" fields={INSIGHT_FIELDS} testId="insights" columns={[{ key: "title", label: "Title" }, { key: "tag", label: "Tag" }]} /> },
    ],
  },
  {
    group: "Career Side",
    items: [
      { id: "applications", label: "Applications", icon: Users, el: <ApplicationsTab /> },
      { id: "jobs", label: "Job Openings", icon: Briefcase, el: <CrudTab endpoint="jobs" title="Job Openings" fields={JOB_FIELDS} testId="jobs" columns={[{ key: "title", label: "Title" }, { key: "department", label: "Department" }, { key: "status", label: "Status" }]} /> },
      { id: "hiring-stages", label: "Hiring Process", icon: Workflow, el: <CrudTab endpoint="hiring-stages" title="Hiring Process Stages" fields={STAGE_FIELDS} testId="stages" columns={[{ key: "title", label: "Stage" }, { key: "order", label: "Order" }]} /> },
      { id: "departments", label: "Departments & Locations", icon: Globe, el: <CrudTab endpoint="departments" title="Departments & Locations" fields={DEPARTMENT_FIELDS} testId="departments" columns={[{ key: "name", label: "Name" }]} /> },
    ],
  },
  {
    group: "General",
    items: [
      { id: "leadership", label: "Leadership", icon: UsersRound, el: <CrudTab endpoint="leadership" title="Leadership" fields={LEADERSHIP_FIELDS} testId="leadership" columns={[{ key: "name", label: "Name" }, { key: "role", label: "Role" }]} /> },
      { id: "settings", label: "Contact, Social & SEO", icon: LayoutDashboard, el: <SettingsTab /> },
    ],
  },
];

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("leads");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("tark_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("tark_token");
        navigate("/admin/login");
      })
      .finally(() => setChecking(false));
  }, [navigate]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("tark_token");
    navigate("/admin/login");
  };

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-tark-bg font-mono text-sm text-tark-dim">Loading…</div>;
  if (!user) return null;

  const active = SECTIONS.flatMap((s) => s.items).find((i) => i.id === tab);

  return (
    <div className="flex min-h-screen bg-tark-bg text-tark-ink" data-testid="admin-dashboard">
      <aside className="fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-tark-line bg-tark-surface/60">
        <div className="flex items-center gap-3 border-b border-tark-line px-6 py-5">
          <img src="/tark-logo.png" alt="TARK" className="h-8 w-auto" />
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-tark-dim">Admin Console</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-4 py-5">
          {SECTIONS.map((section) => (
            <div key={section.group} className="mb-6">
              <p className="px-3 pb-2 font-mono text-[9px] uppercase tracking-[0.3em] text-tark-dim">{section.group}</p>
              {section.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setTab(item.id)}
                  data-testid={`admin-nav-${item.id}`}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left font-display text-sm font-semibold transition-colors ${
                    tab === item.id ? "bg-tark-blue/15 text-tark-cyan" : "text-tark-mut hover:bg-tark-card hover:text-tark-ink"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="border-t border-tark-line p-4">
          <p className="truncate px-2 font-mono text-[10px] text-tark-dim">{user.email}</p>
          <button onClick={logout} data-testid="admin-logout"
                  className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-tark-mut transition-colors hover:text-red-400">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="ml-64 flex-1 px-8 py-10 lg:px-12">
        <div className="mx-auto max-w-5xl">{active?.el}</div>
      </main>
    </div>
  );
};

export default AdminDashboard;
